import { useState } from 'react';

import heroVideo from '@/assets/videos/hero_bg_av1.mp4';
import { cn } from '@/utils/cn';

/**
 * The fluid surface behind the hero.
 *
 * The design renders this as moving footage, so that is what ships: an AV1 mp4
 * imported through Vite, which hashes and emits it like any other asset.
 *
 * The CSS wave below is not dead code -- it is what plays when the video
 * cannot. A looping full-bleed background is exactly the kind of motion
 * `prefers-reduced-motion` exists to stop, and there is no pause control on a
 * decorative backdrop to offer instead, so the honest answer is not to load
 * the video at all for those readers. Four overlapping radial gradients stand
 * in for the mass of the wave and a blurred stroke for the light ridge running
 * through it; the palette and the direction of the flow are right, the surface
 * detail is not.
 */
const WAVE = [
  // The crest, high on the right where the surface catches the most light.
  'radial-gradient(46% 56% at 74% 40%, #8B63EE 0%, rgba(139, 99, 238, 0) 68%)',
  // The main body of the wave, low and wide.
  'radial-gradient(62% 72% at 58% 76%, #7C4DEA 0%, rgba(124, 77, 234, 0) 70%)',
  // The trough on the left, darker and further back.
  'radial-gradient(40% 50% at 10% 64%, #6132C9 0%, rgba(97, 50, 201, 0) 72%)',
].join(', ');

/**
 * Sits above whichever backdrop is drawn, so the hero copy keeps its contrast
 * over the brightest frame of the footage.
 *
 * It falls to `--color-night`, the same fill every section below the hero
 * uses, and not to the deeper `#12021C` it used to. That difference is what
 * put a hard line across the page: the hero's bottom edge was crushing to
 * near-black while the section under it started at #28003C, so the two met as
 * a visible band instead of one continuing surface. Held off until 52% as
 * well, which keeps the footage out of the vignette until it is genuinely near
 * an edge.
 */
const VIGNETTE = 'radial-gradient(96% 96% at 50% 46%, rgba(40, 0, 60, 0) 52%, #28003C 100%)';

/**
 * How far the footage is scaled past `object-cover`.
 *
 * The wave occupies a band across the middle of the frame, not the whole of
 * it, and the hero here is a full viewport tall where the design's is closer
 * to 1.9:1. Covering alone therefore parks the wave in the middle third and
 * leaves the bottom of the hero empty. Zooming past cover pushes the band out
 * to the edges the way the design has it.
 *
 * It is also what holds the wave clear of the header. The footage keeps its
 * top eighth empty, and both the cover fit and this zoom are anchored to the
 * top edge, so the crest can never start higher than an eighth of the framed
 * height times this number -- about 126px at the shortest viewport the site is
 * built for, comfortably below the 112px the header occupies. Anchored to the
 * centre instead, as it was, the same eighth landed 5px from the top and the
 * wave ran straight through the nav.
 *
 * This is the knob to turn if the hero's height changes or the footage is
 * recut: higher fills more, crops more off the sides, and pushes the crest
 * further down the page.
 */
const ZOOM = 1.3;

export function HeroBackdrop({ className }) {
  // Read once, at mount. The preference does not change mid-session in any way
  // worth re-rendering for, and reading it in state rather than an effect means
  // the video element is never created for a reader who asked for stillness --
  // as opposed to created, played, and then hidden.
  const [playsVideo] = useState(
    () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );

  return (
    <div
      aria-hidden="true"
      // `isolate` so the video's blend below resolves against this element's
      // own `bg-night` and stops there, rather than reaching through to
      // whatever the page has painted behind the hero.
      className={cn(
        'bg-night pointer-events-none absolute inset-0 isolate overflow-hidden',
        className,
      )}
    >
      {playsVideo ? (
        <video
          // `mix-blend-lighten` against the `bg-night` behind it: the footage
          // is a bright wave on black, and left alone that black is what put a
          // hard line across the page -- the hero ended in near-black while the
          // section under it starts at #28003C. Lighten takes the brighter of
          // the two per channel, so every pixel darker than the site's own
          // ground is lifted to it and every pixel of the wave is untouched.
          // A tint or an opacity fade would have dulled the wave to fix the
          // background; this only moves the floor.
          // `object-top` and a top transform origin together: whatever the
          // viewport's shape, the footage's own top edge is pinned to the top
          // of the hero and everything it overflows is taken off the bottom.
          // That makes the crest's distance from the header a fixed fraction
          // of the frame rather than something that drifts with the aspect
          // ratio, and it cannot uncover the bottom of the hero either, since
          // the covered height is never less than the hero's to begin with.
          className="absolute inset-0 h-full w-full object-cover object-top mix-blend-lighten"
          style={{ transform: `scale(${ZOOM})`, transformOrigin: 'top' }}
          src={heroVideo}
          // All four are load-bearing for autoplay: browsers refuse to start
          // an unmuted video on their own, and iOS refuses to play one inline
          // without `playsInline` -- it would open the fullscreen player over
          // the page instead.
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <>
          <div className="absolute inset-0" style={{ backgroundImage: WAVE }} />

          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1440 760"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <filter id="hero-ridge" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="12" />
              </filter>
            </defs>
            <path
              d="M -60 520 C 180 500, 250 320, 470 470 C 690 620, 830 300, 1110 195 C 1260 140, 1390 165, 1500 215"
              fill="none"
              stroke="#C4B0FA"
              strokeWidth="3"
              opacity="0.5"
              filter="url(#hero-ridge)"
            />
            <path
              d="M -60 560 C 200 560, 300 400, 520 530 C 740 660, 880 380, 1140 270"
              fill="none"
              stroke="#A88CF5"
              strokeWidth="2"
              opacity="0.3"
              filter="url(#hero-ridge)"
            />
          </svg>
        </>
      )}

      <div className="absolute inset-0" style={{ backgroundImage: VIGNETTE }} />
    </div>
  );
}
